import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Shield, 
  PlusCircle, 
  FileText, 
  CheckCircle2, 
  Bell, 
  BookOpen, 
  FileCheck, 
  User, 
  KeyRound, 
  LogOut, 
  ChevronLeft, 
  ChevronRight, 
  Menu, 
  X
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { ThemeToggle } from '@/components/theme-toggle';

export function UserDashboardLayout() {
  const { user, profile, organization, signOut, session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('user_sidebar_collapsed') === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const toggleSidebar = () => {
    setCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('user_sidebar_collapsed', String(next));
      return next;
    });
  };

  // Close mobile drawer on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Fetch unread notification count
  useEffect(() => {
    const fetchNotifCount = async () => {
      try {
        const token = session?.access_token || localStorage.getItem('compass_token');
        if (!token) return;
        const res = await fetch('/api/user/notifications', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.success && typeof data.unread_count === 'number') {
            setUnreadCount(data.unread_count);
          }
        }
      } catch (e) {}
    };

    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 30000);
    return () => clearInterval(interval);
  }, [session]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  // Role resolution
  const userRole = (user?.role || profile?.role || '').toLowerCase();
  const userEmail = (user?.email || '').toLowerCase();
  const isAdmin = userRole.includes('admin') || userRole.includes('super admin') || userEmail.includes('achakkaravarthy');

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      {/* Mobile Backdrop */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-xs lg:hidden transition-opacity"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside 
        style={{ 
          width: collapsed ? '72px' : '260px', 
          minWidth: collapsed ? '72px' : '260px',
          maxWidth: collapsed ? '72px' : '260px'
        }}
        className={cn(
          "flex flex-col border-r border-border bg-card shrink-0 h-screen transition-all duration-200 ease-in-out z-30",
          mobileOpen ? "fixed inset-y-0 left-0" : "hidden lg:flex"
        )}
      >
        {/* Brand Header */}
        <div className="flex h-16 items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-md shadow-blue-500/20">
              <Shield className="h-5 w-5" />
            </div>
            {!collapsed && (
              <div className="flex flex-col overflow-hidden">
                <span className="font-bold text-sm tracking-tight truncate">SOC Portal</span>
                <span className="text-[11px] text-muted-foreground font-medium truncate">Detection Governance</span>
              </div>
            )}
          </div>

          <button 
            onClick={toggleSidebar}
            className="hidden lg:flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>

          <button 
            onClick={() => setMobileOpen(false)}
            className="lg:hidden h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation Groups */}
        <div className="flex-1 overflow-y-auto px-3 py-4 space-y-6 custom-scrollbar">
          
          {/* GROUP 1: DASHBOARD */}
          <div>
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                Dashboard
              </p>
            )}
            <nav className="space-y-1">
              <NavItem 
                to="/user/dashboard" 
                icon={LayoutDashboard} 
                label="Dashboard Home" 
                collapsed={collapsed} 
              />
            </nav>
          </div>

          {/* GROUP 2: MY USE CASES */}
          <div>
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                My Use Cases
              </p>
            )}
            <nav className="space-y-1">
              <NavItem 
                to="/user/use-cases" 
                icon={Shield} 
                label="My Use Cases" 
                collapsed={collapsed} 
              />
              <NavItem 
                to="/user/use-cases/create" 
                icon={PlusCircle} 
                label="Create Use Case" 
                collapsed={collapsed} 
                accent
              />
              <NavItem 
                to="/user/use-cases/drafts" 
                icon={FileText} 
                label="Drafts" 
                collapsed={collapsed} 
              />
              <NavItem 
                to="/user/use-cases/completed" 
                icon={CheckCircle2} 
                label="My Completed Use Cases" 
                collapsed={collapsed} 
              />
            </nav>
          </div>

          {/* GROUP 3: NOTIFICATIONS */}
          <div>
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                Notifications
              </p>
            )}
            <nav className="space-y-1">
              <NavItem 
                to="/user/notifications" 
                icon={Bell} 
                label="Notifications" 
                badge={unreadCount > 0 ? unreadCount : undefined} 
                collapsed={collapsed} 
              />
            </nav>
          </div>

          {/* GROUP 5: KNOWLEDGE */}
          <div>
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                Knowledge
              </p>
            )}
            <nav className="space-y-1">
              <NavItem 
                to="/user/knowledge-base" 
                icon={BookOpen} 
                label="Knowledge Base" 
                collapsed={collapsed} 
              />
              <NavItem 
                to="/user/guidelines" 
                icon={FileCheck} 
                label="Guidelines" 
                collapsed={collapsed} 
              />
            </nav>
          </div>

          {/* GROUP 6: ACCOUNT */}
          <div className="pt-2 border-t border-border">
            {!collapsed && (
              <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground/70">
                Account
              </p>
            )}
            <nav className="space-y-1">
              <NavItem 
                to="/user/profile" 
                icon={User} 
                label="My Profile" 
                collapsed={collapsed} 
              />
              <NavItem 
                to="/user/change-password" 
                icon={KeyRound} 
                label="Change Password" 
                collapsed={collapsed} 
              />
              <button
                onClick={handleSignOut}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-xs font-medium rounded-lg text-rose-500 hover:bg-rose-500/10 transition-colors",
                  collapsed && "justify-center px-0"
                )}
                title={collapsed ? "Sign Out" : undefined}
              >
                <LogOut className="h-4 w-4 shrink-0" />
                {!collapsed && <span>Sign Out</span>}
              </button>
            </nav>
          </div>

        </div>

        {/* User Footer Card */}
        <div className="p-3 border-t border-border bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 shrink-0 rounded-full bg-blue-600/20 text-blue-500 flex items-center justify-center font-bold text-xs border border-blue-500/30">
              {(profile?.full_name || user?.displayName || user?.email || 'U')[0].toUpperCase()}
            </div>
            {!collapsed && (
              <div className="flex flex-col min-w-0 flex-1">
                <span className="text-xs font-semibold truncate text-foreground">
                  {profile?.full_name || user?.displayName || user?.email?.split('@')[0]}
                </span>
                <span className="text-[11px] text-muted-foreground truncate">
                  {user?.role || profile?.role || 'Normal User'}
                </span>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Top Navbar */}
        <header className="flex h-16 items-center justify-between px-4 lg:px-6 border-b border-border bg-card/60 backdrop-blur-md shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-muted/50 text-foreground hover:bg-muted"
            >
              <Menu className="h-5 w-5" />
            </button>
            
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">SOC Portal</span>
              <span>/</span>
              <span className="text-blue-500 font-medium capitalize">
                {location.pathname.replace('/user/', '').replace(/-/g, ' ') || 'Dashboard'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Quick Action: Admin Console for Admins */}
            {isAdmin && (
              <button
                onClick={() => navigate('/admin/soc')}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-border bg-card hover:bg-muted text-foreground transition-all shadow-xs"
                title="Switch to SOC Governance Admin Portal"
              >
                <Shield className="h-3.5 w-3.5 text-blue-500" />
                <span>Admin Console</span>
              </button>
            )}

            {/* Quick Action: New Use Case */}
            <button
              onClick={() => navigate('/user/use-cases/create')}
              className="hidden sm:flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 shadow-sm shadow-blue-500/20 transition-all"
            >
              <PlusCircle className="h-4 w-4" />
              <span>Create Use Case</span>
            </button>

            {/* Notifications Bell */}
            <NavLink
              to="/user/notifications"
              className="relative flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              title="Notifications"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center px-1 rounded-full bg-rose-500 text-[10px] font-bold text-white shadow-xs">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </NavLink>

            {/* Theme Toggle */}
            <ThemeToggle />

            {/* Org pill */}
            <div className="hidden md:flex items-center gap-2 px-2.5 py-1 rounded-lg bg-muted/60 border border-border text-[11px] font-medium text-muted-foreground">
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>{organization?.name || 'Pinochle AI'}</span>
            </div>
          </div>
        </header>

        {/* Dynamic Nested View */}
        <main className="flex-1 min-w-0 overflow-y-auto bg-background/50 p-4 lg:p-6 custom-scrollbar">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

// NavItem Component
function NavItem({ 
  to, 
  icon: Icon, 
  label, 
  badge, 
  accent, 
  collapsed 
}: { 
  to: string; 
  icon: any; 
  label: string; 
  badge?: number | string; 
  accent?: boolean; 
  collapsed?: boolean;
}) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) => cn(
        "group relative flex items-center gap-3 px-3 py-2 text-xs font-medium rounded-lg transition-all",
        isActive 
          ? "bg-blue-600/10 text-blue-600 font-semibold border-l-3 border-blue-600 shadow-xs dark:bg-blue-500/15 dark:text-blue-400" 
          : "text-muted-foreground hover:bg-muted/80 hover:text-foreground",
        accent && !isActive && "text-blue-500 hover:text-blue-600",
        collapsed && "justify-center px-0"
      )}
      title={collapsed ? label : undefined}
    >
      <Icon className={cn("h-4 w-4 shrink-0 transition-transform group-hover:scale-105", accent && "text-blue-500")} />
      {!collapsed && <span className="truncate">{label}</span>}
      {!collapsed && badge !== undefined && (
        <span className="ml-auto flex h-4 min-w-4 items-center justify-center px-1 rounded-full bg-rose-500 text-[10px] font-bold text-white">
          {badge}
        </span>
      )}
      {collapsed && badge !== undefined && (
        <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-rose-500" />
      )}
    </NavLink>
  );
}
